import { supabase } from './supabase';
import { SendGridService } from './sendgridService';
import {
  TrustedAdvisor,
  AdvisorPermission,
  UnlockRequest,
  AdvisorAuditLog,
  EmergencyContact,
  AdvisorInvitation,
  AdvisorNotification,
  CreateTrustedAdvisorRequest,
  UpdateTrustedAdvisorRequest,
  CreateAdvisorPermissionRequest,
  UpdateAdvisorPermissionRequest,
  CreateUnlockRequestRequest,
  BlockUnlockRequestRequest,
  CreateEmergencyContactRequest,
  UpdateEmergencyContactRequest,
  SendInvitationRequest,
  AcceptInvitationRequest,
  TrustedAdvisorDashboard,
  TrustedAdvisorSummary,
  AdvisorPermissionsSummary,
  UnlockRequestSummary,
  DocumentPermissionMatrix,
  BulkPermissionUpdateRequest,
  TrustedAdvisorAnalytics,
  TrustedAdvisorFilters,
  AuditLogFilters,
  UnlockRequestFilters,
  PermissionType,
  ViewMode,
  AdvisorStatus,
  UnlockRequestStatus,
  InvitationStatus,
  EmergencyContactAccessLevel,
  NotificationType,
  AuditAction,
  DocumentType
} from '../types/trustedAdvisor';

export class TrustedAdvisorService {
  // ===== TRUSTED ADVISOR MANAGEMENT =====

  // Create a new trusted advisor
  static async createTrustedAdvisor(request: CreateTrustedAdvisorRequest): Promise<TrustedAdvisor> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Validate email format
    if (!SendGridService.isValidEmail(request.email)) {
      throw new Error('Invalid email format');
    }

    // Check if advisor already exists for this user
    const { data: existingAdvisor } = await supabase
      .from('trusted_advisors')
      .select('id')
      .eq('user_id', user.id)
      .eq('email', request.email)
      .single();

    if (existingAdvisor) {
      throw new Error('A trusted advisor with this email already exists');
    }

    // Create the trusted advisor
    const { data: advisor, error } = await supabase
      .from('trusted_advisors')
      .insert({
        user_id: user.id,
        first_name: request.first_name,
        last_name: request.last_name,
        email: request.email,
        phone: request.phone,
        relationship: request.relationship,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    // Create invitation in the new system
    const { data: tokenData } = await supabase.rpc('generate_secure_invitation_token');
    const invitationToken = tokenData;

    // Create invitation
    const { data: invitation, error: invitationError } = await supabase
      .from('trusted_advisor_invitations')
      .insert({
        inviter_id: user.id,
        invited_email: request.email,
        invited_first_name: request.first_name,
        invited_last_name: request.last_name,
        invited_phone: request.phone,
        relationship: request.relationship,
        invitation_token: invitationToken,
        invitation_message: request.invitation_message,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
      })
      .select()
      .single();

    if (invitationError) throw invitationError;

    // Send invitation email
    const invitationLink = `${import.meta.env.VITE_APP_URL}/trusted-advisor/invite/${invitationToken}`;
    await SendGridService.sendTrustedAdvisorInvitation({
      advisor_name: request.email,
      user_name: user.email || 'User',
      invitation_link: invitationLink,
      invitation_message: request.invitation_message,
      expires_at: invitation.expires_at
    });

    // Send notification to the invited person (if they have an account)
    try {
      const { data: invitedUser } = await supabase
        .from('auth.users')
        .select('id')
        .eq('email', request.email)
        .single();

      if (invitedUser) {
        await supabase.rpc('send_user_notification', 
          invitedUser.id,
          'trusted_advisor_invitation',
          'Trusted Advisor Invitation',
          `${user.email || 'Someone'} has invited you to be a trusted advisor.`,
          'medium',
          invitationLink,
          'View Invitation'
        );
      }
    } catch (error) {
      // If user doesn't exist or notification fails, continue
      console.log('Could not send notification to invited user:', error);
    }

    return advisor;
  }

  // Get all trusted advisors for the current user
  static async getTrustedAdvisors(filters?: TrustedAdvisorFilters): Promise<TrustedAdvisor[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('trusted_advisors')
      .select('*')
      .eq('user_id', user.id);

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.relationship) {
      query = query.eq('relationship', filters.relationship);
    }

    if (filters?.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    // Filter by permissions if requested
    if (filters?.has_permissions !== undefined) {
      const advisorsWithPermissions = await this.getAdvisorsWithPermissions();
      const advisorIdsWithPermissions = new Set(advisorsWithPermissions.map(a => a.advisor_id));

      return data.filter(advisor => 
        filters.has_permissions ? advisorIdsWithPermissions.has(advisor.id) : !advisorIdsWithPermissions.has(advisor.id)
      );
    }

    return data || [];
  }

  // Update trusted advisor
  static async updateTrustedAdvisor(request: UpdateTrustedAdvisorRequest): Promise<TrustedAdvisor> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership
    const { data: existingAdvisor } = await supabase
      .from('trusted_advisors')
      .select('*')
      .eq('id', request.advisor_id)
      .eq('user_id', user.id)
      .single();

    if (!existingAdvisor) {
      throw new Error('Trusted advisor not found or not authorized');
    }

    const updateData: Partial<TrustedAdvisor> = {};
    if (request.first_name !== undefined) updateData.first_name = request.first_name;
    if (request.last_name !== undefined) updateData.last_name = request.last_name;
    if (request.relationship !== undefined) updateData.relationship = request.relationship;

    const { data: advisor, error } = await supabase
      .from('trusted_advisors')
      .update(updateData)
      .eq('id', request.advisor_id)
      .select()
      .single();

    if (error) throw error;

    return advisor;
  }

  // Remove trusted advisor
  static async removeTrustedAdvisor(advisorId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership
    const { data: existingAdvisor } = await supabase
      .from('trusted_advisors')
      .select('*')
      .eq('id', advisorId)
      .eq('user_id', user.id)
      .single();

    if (!existingAdvisor) {
      throw new Error('Trusted advisor not found or not authorized');
    }

    // Soft delete by setting status to removed
    const { error } = await supabase
      .from('trusted_advisors')
      .update({ 
        status: 'removed',
        removed_at: new Date().toISOString()
      })
      .eq('id', advisorId);

    if (error) throw error;

    // Remove all permissions for this advisor
    await this.removeAllAdvisorPermissions(advisorId);
  }

  // ===== PERMISSION MANAGEMENT =====

  // Create advisor permission
  static async createAdvisorPermission(request: CreateAdvisorPermissionRequest): Promise<AdvisorPermission> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify advisor ownership
    const { data: advisor } = await supabase
      .from('trusted_advisors')
      .select('*')
      .eq('id', request.advisor_id)
      .eq('user_id', user.id)
      .single();

    if (!advisor) {
      throw new Error('Trusted advisor not found or not authorized');
    }

    // Check if permission already exists
    const { data: existingPermission } = await supabase
      .from('advisor_permissions')
      .select('*')
      .eq('advisor_id', request.advisor_id)
      .eq('document_id', request.document_id)
      .single();

    if (existingPermission) {
      throw new Error('Permission already exists for this advisor and document');
    }

    const { data: permission, error } = await supabase
      .from('advisor_permissions')
      .insert({
        advisor_id: request.advisor_id,
        document_id: request.document_id,
        document_type: request.document_type,
        permission_type: request.permission_type,
        view_mode: request.view_mode,
        unlocker_id: request.unlocker_id,
        unlock_wait_period_days: request.unlock_wait_period_days || 30
      })
      .select()
      .single();

    if (error) throw error;

    return permission;
  }

  // Update advisor permission
  static async updateAdvisorPermission(request: UpdateAdvisorPermissionRequest): Promise<AdvisorPermission> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify permission ownership
    const { data: existingPermission } = await supabase
      .from('advisor_permissions')
      .select(`
        *,
        trusted_advisors!inner(
          trusted_advisors!inner(user_id)
        )
      `)
      .eq('id', request.permission_id)
      .eq('trusted_advisors.user_id', user.id)
      .single();

    if (!existingPermission) {
      throw new Error('Permission not found or not authorized');
    }

    const updateData: Partial<AdvisorPermission> = {};
    if (request.permission_type !== undefined) updateData.permission_type = request.permission_type;
    if (request.view_mode !== undefined) updateData.view_mode = request.view_mode;
    if (request.unlocker_id !== undefined) updateData.unlocker_id = request.unlocker_id;
    if (request.unlock_wait_period_days !== undefined) updateData.unlock_wait_period_days = request.unlock_wait_period_days;
    if (request.is_active !== undefined) updateData.is_active = request.is_active;

    const { data: permission, error } = await supabase
      .from('advisor_permissions')
      .update(updateData)
      .eq('id', request.permission_id)
      .select()
      .single();

    if (error) throw error;

    return permission;
  }

  // Get advisor permissions
  static async getAdvisorPermissions(advisorId?: string): Promise<AdvisorPermission[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First get the user's trusted advisors
    const { data: advisors } = await supabase
      .from('trusted_advisors')
      .select('id')
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (!advisors || advisors.length === 0) {
      return [];
    }

    const advisorIds = advisors.map(a => a.id);

    let query = supabase
      .from('advisor_permissions')
      .select('*')
      .in('advisor_id', advisorIds)
      .eq('is_active', true);

    if (advisorId) {
      query = query.eq('advisor_id', advisorId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  // Remove all permissions for an advisor
  static async removeAllAdvisorPermissions(advisorId: string): Promise<void> {
    const { error } = await supabase
      .from('advisor_permissions')
      .update({ is_active: false })
      .eq('advisor_id', advisorId);

    if (error) throw error;
  }

  // ===== UNLOCK REQUESTS =====

  // Create unlock request
  static async createUnlockRequest(request: CreateUnlockRequestRequest): Promise<UnlockRequest> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get advisor ID for current user
    const { data: advisor } = await supabase
      .from('trusted_advisors')
      .select('id')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .single();

    if (!advisor) {
      throw new Error('No active trusted advisor found');
    }

    // Check if permission exists and allows unlock requests
    const { data: permission } = await supabase
      .from('advisor_permissions')
      .select('*')
      .eq('advisor_id', advisor.id)
      .eq('document_id', request.document_id)
      .eq('is_active', true)
      .single();

    if (!permission) {
      throw new Error('No permission found for this document');
    }

    if (permission.permission_type !== 'share_after_death') {
      throw new Error('Document is not configured for post-death sharing');
    }

    // Check if unlock request already exists
    const { data: existingRequest } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('advisor_id', advisor.id)
      .eq('document_id', request.document_id)
      .in('status', ['pending', 'approved'])
      .single();

    if (existingRequest) {
      throw new Error('Unlock request already exists for this document');
    }

    const { data: unlockRequest, error } = await supabase
      .from('unlock_requests')
      .insert({
        advisor_id: advisor.id,
        document_id: request.document_id,
        document_type: request.document_type,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    return unlockRequest;
  }

  // Block unlock request
  static async blockUnlockRequest(request: BlockUnlockRequestRequest): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership of the document
    const { data: unlockRequest } = await supabase
      .from('unlock_requests')
      .select(`
        *,
        advisor_permissions!inner(
          trusted_advisors!inner(user_id)
        )
      `)
      .eq('id', request.unlock_request_id)
      .eq('advisor_permissions.trusted_advisors.user_id', user.id)
      .single();

    if (!unlockRequest) {
      throw new Error('Unlock request not found or not authorized');
    }

    const { error } = await supabase
      .from('unlock_requests')
      .update({
        status: 'blocked',
        blocked_by_user: true,
        blocked_at: new Date().toISOString()
      })
      .eq('id', request.unlock_request_id);

    if (error) throw error;
  }

  // Get unlock requests
  static async getUnlockRequests(filters?: UnlockRequestFilters): Promise<UnlockRequestSummary[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('unlock_requests_summary')
      .select('*')
      .eq('user_id', user.id);

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.advisor_id) {
      query = query.eq('advisor_id', filters.advisor_id);
    }

    if (filters?.document_type) {
      query = query.eq('document_type', filters.document_type);
    }

    if (filters?.date_from) {
      query = query.gte('requested_at', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('requested_at', filters.date_to);
    }

    const { data, error } = await query.order('requested_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ===== EMERGENCY CONTACTS =====

  // Create emergency contact
  static async createEmergencyContact(request: CreateEmergencyContactRequest): Promise<EmergencyContact> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: contact, error } = await supabase
      .from('emergency_contacts')
      .insert({
        user_id: user.id,
        name: request.name,
        email: request.email,
        phone: request.phone,
        relationship: request.relationship,
        access_level: request.access_level
      })
      .select()
      .single();

    if (error) throw error;

    return contact;
  }

  // Update emergency contact
  static async updateEmergencyContact(request: UpdateEmergencyContactRequest): Promise<EmergencyContact> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership
    const { data: existingContact } = await supabase
      .from('emergency_contacts')
      .select('*')
      .eq('id', request.contact_id)
      .eq('user_id', user.id)
      .single();

    if (!existingContact) {
      throw new Error('Emergency contact not found or not authorized');
    }

    const updateData: Partial<EmergencyContact> = {};
    if (request.name !== undefined) updateData.name = request.name;
    if (request.email !== undefined) updateData.email = request.email;
    if (request.phone !== undefined) updateData.phone = request.phone;
    if (request.relationship !== undefined) updateData.relationship = request.relationship;
    if (request.access_level !== undefined) updateData.access_level = request.access_level;
    if (request.is_active !== undefined) updateData.is_active = request.is_active;

    const { data: contact, error } = await supabase
      .from('emergency_contacts')
      .update(updateData)
      .eq('id', request.contact_id)
      .select()
      .single();

    if (error) throw error;

    return contact;
  }

  // Get emergency contacts
  static async getEmergencyContacts(): Promise<EmergencyContact[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('emergency_contacts')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ===== INVITATION MANAGEMENT =====

  // Send invitation
  static async sendInvitation(advisorId: string, message?: string): Promise<AdvisorInvitation> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get advisor details
    const { data: advisor } = await supabase
      .from('trusted_advisors')
      .select('*')
      .eq('id', advisorId)
      .eq('user_id', user.id)
      .single();

    if (!advisor) {
      throw new Error('Trusted advisor not found or not authorized');
    }

    // Generate invitation token
    const { data: tokenData } = await supabase.rpc('generate_secure_invitation_token');
    const invitationToken = tokenData;

    // Create invitation in the new system
    const { data: invitation, error } = await supabase
      .from('trusted_advisor_invitations')
      .insert({
        inviter_id: user.id,
        invited_email: advisor.email,
        invited_first_name: advisor.first_name,
        invited_last_name: advisor.last_name,
        invited_phone: advisor.phone,
        relationship: advisor.relationship,
        invitation_token: invitationToken,
        invitation_message: message,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
      })
      .select()
      .single();

    if (error) throw error;

    // Send invitation email
    const invitationLink = `${import.meta.env.VITE_APP_URL}/trusted-advisor/invite/${invitationToken}`;
    await SendGridService.sendTrustedAdvisorInvitation({
      advisor_name: advisor.email,
      user_name: user.email || 'User',
      invitation_link: invitationLink,
      invitation_message: message,
      expires_at: invitation.expires_at
    });

    // Send notification to the invited person (if they have an account)
    try {
      const { data: invitedUser } = await supabase
        .from('auth.users')
        .select('id')
        .eq('email', advisor.email)
        .single();

      if (invitedUser) {
        await supabase.rpc('send_user_notification', 
          invitedUser.id,
          'trusted_advisor_invitation',
          'Trusted Advisor Invitation',
          `${user.email || 'Someone'} has invited you to be a trusted advisor.`,
          'medium',
          invitationLink,
          'View Invitation'
        );
      }
    } catch (error) {
      // If user doesn't exist or notification fails, continue
      console.log('Could not send notification to invited user:', error);
    }

    return invitation;
  }

  // Accept invitation
  static async acceptInvitation(request: AcceptInvitationRequest): Promise<void> {
    // Get invitation from the new system
    const { data: invitation } = await supabase
      .from('trusted_advisor_invitations')
      .select('*')
      .eq('invitation_token', request.invitation_token)
      .eq('status', 'pending')
      .single();

    if (!invitation) {
      throw new Error('Invalid or expired invitation');
    }

    if (new Date() > new Date(invitation.expires_at)) {
      throw new Error('Invitation has expired');
    }

    // Use the new function to process the invitation
    const { data: success, error } = await supabase.rpc(
      'process_trusted_advisor_invitation',
      invitation.id,
      'accept',
      request.accepted_by_user_id
    );

    if (error || !success) {
      throw new Error('Failed to process invitation acceptance');
    }

    // Send email notification
    await SendGridService.sendInvitationAcceptedNotification(
      invitation.invited_email,
      invitation.invited_first_name || 'User',
      `${invitation.invited_first_name} ${invitation.invited_last_name}`
    );
  }

  // ===== DASHBOARD & ANALYTICS =====

  // Get dashboard data
  static async getDashboard(): Promise<TrustedAdvisorDashboard> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      console.log('🔍 Loading trusted advisor dashboard for user:', user.id);

      // Get summary
      let summary = null;
      try {
        const { data: summaryData, error: summaryError } = await supabase
          .from('user_trusted_advisors_summary')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (summaryError) {
          console.warn('⚠️ Could not load summary, using defaults:', summaryError);
        } else {
          summary = summaryData;
        }
      } catch (error) {
        console.warn('⚠️ Summary view not available, using defaults');
      }

      // Get advisors
      let advisors = [];
      try {
        advisors = await this.getTrustedAdvisors();
      } catch (error) {
        console.warn('⚠️ Could not load advisors:', error);
      }

      // Get permissions summary
      let permissionsSummary = [];
      try {
        const { data: permissionsData, error: permissionsError } = await supabase
          .from('advisor_permissions_summary')
          .select('*')
          .eq('user_id', user.id);

        if (permissionsError) {
          console.warn('⚠️ Could not load permissions summary:', permissionsError);
        } else {
          permissionsSummary = permissionsData || [];
        }
      } catch (error) {
        console.warn('⚠️ Permissions summary view not available, using defaults');
      }

      // Get pending unlock requests
      let pendingUnlockRequests = [];
      try {
        pendingUnlockRequests = await this.getUnlockRequests({ status: 'pending' });
      } catch (error) {
        console.warn('⚠️ Could not load unlock requests:', error);
      }

      // Get emergency contacts
      let emergencyContacts = [];
      try {
        emergencyContacts = await this.getEmergencyContacts();
      } catch (error) {
        console.warn('⚠️ Could not load emergency contacts:', error);
      }

      // Get recent activity
      let recentActivity = [];
      try {
        recentActivity = await this.getAuditLogs({ limit: 10 });
      } catch (error) {
        console.warn('⚠️ Could not load recent activity:', error);
      }

      // Get unread notifications count
      let unreadNotifications = 0;
      try {
        const { count: notificationsCount, error: notificationsError } = await supabase
          .from('advisor_notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_read', false);

        if (notificationsError) {
          console.warn('⚠️ Could not load notifications count:', notificationsError);
        } else {
          unreadNotifications = notificationsCount || 0;
        }
      } catch (error) {
        console.warn('⚠️ Notifications table not available, using defaults');
      }

      // Get pending invitations count
      let pendingInvitations = 0;
      try {
        const { count: invitationsCount, error: invitationsError } = await supabase
          .from('trusted_advisor_invitations')
          .select('*', { count: 'exact', head: true })
          .eq('inviter_id', user.id)
          .eq('status', 'pending');

        if (invitationsError) {
          console.warn('⚠️ Could not load pending invitations count:', invitationsError);
        } else {
          pendingInvitations = invitationsCount || 0;
        }
      } catch (error) {
        console.warn('⚠️ Invitations table not available, using defaults');
      }

      const dashboardData = {
        summary: summary || {
          total_advisors: 0,
          active_advisors: 0,
          pending_advisors: 0,
          removed_advisors: 0
        },
        advisors,
        permissions_summary: permissionsSummary || [],
        pending_unlock_requests: pendingUnlockRequests,
        emergency_contacts: emergencyContacts,
        recent_activity: recentActivity,
        unread_notifications: unreadNotifications || 0,
        pending_invitations_count: pendingInvitations || 0
      };

      console.log('✅ Dashboard loaded successfully:', dashboardData);
      return dashboardData;
    } catch (error) {
      console.error('❌ Error loading dashboard:', error);
      throw error;
    }
  }

  // Get analytics
  static async getAnalytics(): Promise<TrustedAdvisorAnalytics> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // This would typically involve complex queries and aggregations
    // For now, returning a basic structure
    return {
      total_advisors: 0,
      active_advisors: 0,
      pending_invitations: 0,
      documents_shared: 0,
      unlock_requests_pending: 0,
      unlock_requests_completed: 0,
      permission_changes_this_month: 0,
      most_shared_documents: [],
      advisor_activity_timeline: [],
      permission_distribution: {
        share_now: 0,
        share_after_death: 0,
        do_not_share: 0
      },
      view_mode_distribution: {
        view_only: 0,
        view_and_comment: 0,
        view_and_edit: 0,
        full_access: 0
      }
    };
  }

  // ===== AUDIT LOGS =====

  // Get audit logs
  static async getAuditLogs(filters?: AuditLogFilters & { limit?: number }): Promise<AdvisorAuditLog[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('advisor_audit_logs')
      .select('*')
      .eq('user_id', user.id);

    if (filters?.action) {
      query = query.eq('action', filters.action);
    }

    if (filters?.advisor_id) {
      query = query.eq('advisor_id', filters.advisor_id);
    }

    if (filters?.document_type) {
      query = query.eq('document_type', filters.document_type);
    }

    if (filters?.date_from) {
      query = query.gte('created_at', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ===== UTILITY METHODS =====

  // Get advisors with permissions
  static async getAdvisorsWithPermissions(): Promise<AdvisorPermissionsSummary[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('advisor_permissions_summary')
      .select('*')
      .eq('user_id', user.id)
      .gt('total_permissions', 0);

    if (error) throw error;
    return data || [];
  }

  // Check if user has access to document
  static async checkDocumentAccess(documentId: string, requiredPermission: ViewMode = 'view_only'): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Check if user is the owner
    // This would need to be implemented based on your document ownership structure

    // Check if user has advisor access
    const { data: advisor } = await supabase
      .from('trusted_advisors')
      .select('id')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .single();

    if (!advisor) return false;

    const { data: result } = await supabase.rpc('check_advisor_document_access', {
      p_advisor_id: advisor.id,
      p_document_id: documentId,
      p_required_permission: requiredPermission
    });

    return result || false;
  }

  // Process pending unlock requests
  static async processPendingUnlockRequests(): Promise<void> {
    const { data: pendingRequests } = await supabase
      .from('unlock_requests')
      .select('id')
      .eq('status', 'pending');

    if (!pendingRequests) return;

    for (const request of pendingRequests) {
      await supabase.rpc('process_unlock_request', {
        p_unlock_request_id: request.id
      });
    }
  }

  // Get documents for permission matrix (real data from all document tables)
  static async getDocumentsForPermissions(): Promise<Array<{ id: string; name: string; type: DocumentType }>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const documents: Array<{ id: string; name: string; type: DocumentType }> = [];

    try {
      // 1. Legal Documents
      const { data: legalDocs } = await supabase
        .from('legal_documents')
        .select('id, title, type')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (legalDocs) {
        legalDocs.forEach(doc => {
          documents.push({
            id: doc.id,
            name: doc.title,
            type: this.mapLegalDocumentType(doc.type)
          });
        });
      }

      // 2. Insurance Documents
      const { data: insuranceDocs } = await supabase
        .from('insurance_documents')
        .select('id, policy_type, provider, policy_number')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (insuranceDocs) {
        insuranceDocs.forEach(doc => {
          documents.push({
            id: doc.id,
            name: `${doc.policy_type} - ${doc.provider} (${doc.policy_number})`,
            type: 'insurance' as DocumentType
          });
        });
      }

      // 3. Financial Accounts
      const { data: financialAccounts } = await supabase
        .from('financial_accounts')
        .select('id, type, institution, account_number_encrypted')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (financialAccounts) {
        financialAccounts.forEach(account => {
          documents.push({
            id: account.id,
            name: `${account.type} - ${account.institution}`,
            type: 'financial_account' as DocumentType
          });
        });
      }

      // 4. Financial Assets
      const { data: financialAssets } = await supabase
        .from('financial_assets')
        .select('id, type, name')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (financialAssets) {
        financialAssets.forEach(asset => {
          documents.push({
            id: asset.id,
            name: `${asset.type} - ${asset.name}`,
            type: 'investment' as DocumentType
          });
        });
      }

      // 5. Medical Records
      const { data: medicalRecords } = await supabase
        .from('medical_records')
        .select('id, record_type, provider_name, record_date')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (medicalRecords) {
        medicalRecords.forEach(record => {
          documents.push({
            id: record.id,
            name: `${record.record_type} - ${record.provider_name} (${new Date(record.record_date).toLocaleDateString()})`,
            type: 'medical_record' as DocumentType
          });
        });
      }

      // 6. Appointments
      const { data: appointments } = await supabase
        .from('health_appointments')
        .select('id, appointment_type, provider_name, appointment_date')
        .eq('user_id', user.id)
        .gte('appointment_date', new Date().toISOString().split('T')[0]);

      if (appointments) {
        appointments.forEach(appointment => {
          documents.push({
            id: appointment.id,
            name: `${appointment.appointment_type} - ${appointment.provider_name} (${new Date(appointment.appointment_date).toLocaleDateString()})`,
            type: 'appointment' as DocumentType
          });
        });
      }

      // 7. Medications
      const { data: medications } = await supabase
        .from('medications')
        .select('id, medication_name, dosage, frequency')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (medications) {
        medications.forEach(med => {
          documents.push({
            id: med.id,
            name: `${med.medication_name} - ${med.dosage} ${med.frequency}`,
            type: 'medication' as DocumentType
          });
        });
      }

      // 8. Pet Profiles
      const { data: pets } = await supabase
        .from('pets')
        .select('id, name, species, breed')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (pets) {
        pets.forEach(pet => {
          documents.push({
            id: pet.id,
            name: `${pet.species} - ${pet.name} (${pet.breed})`,
            type: 'pet_profile' as DocumentType
          });
        });
      }

      // 9. Legacy Items
      const { data: legacyItems } = await supabase
        .from('legacy_items')
        .select('id, title, category')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (legacyItems) {
        legacyItems.forEach(item => {
          documents.push({
            id: item.id,
            name: `${item.category} - ${item.title}`,
            type: 'legacy_item' as DocumentType
          });
        });
      }

      // 10. Digital Assets
      const { data: digitalAssets } = await supabase
        .from('digital_estate_assets')
        .select('id, asset_type, asset_name')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (digitalAssets) {
        digitalAssets.forEach(asset => {
          documents.push({
            id: asset.id,
            name: `${asset.asset_type} - ${asset.asset_name}`,
            type: 'digital_asset' as DocumentType
          });
        });
      }

      // 11. Retirement Accounts
      const { data: retirementAccounts } = await supabase
        .from('retirement_accounts')
        .select('id, retirement_type, provider')
        .eq('user_id', user.id);

      if (retirementAccounts) {
        retirementAccounts.forEach(account => {
          documents.push({
            id: account.id,
            name: `${account.retirement_type} - ${account.provider}`,
            type: 'retirement' as DocumentType
          });
        });
      }

      return documents;

    } catch (error) {
      console.error('Error fetching documents for permissions:', error);
      throw new Error('Failed to fetch documents for permissions');
    }
  }

  // Helper method to map legal document types to DocumentType
  private static mapLegalDocumentType(legalType: string): DocumentType {
    switch (legalType) {
      case 'will':
        return 'will';
      case 'trust':
        return 'trust';
      case 'power_of_attorney':
      case 'poa':
        return 'power_of_attorney';
      case 'advance_directive':
      case 'acd':
        return 'health_directive';
      default:
        return 'will'; // fallback
    }
  }

  // Bulk update permissions for a document
  static async bulkUpdatePermissions(request: BulkPermissionUpdateRequest): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership of all advisors
    const advisorIds = request.advisor_permissions.map(p => p.advisor_id);
    const { data: advisors } = await supabase
      .from('trusted_advisors')
      .select('id')
      .eq('user_id', user.id)
      .in('id', advisorIds);

    if (!advisors || advisors.length !== advisorIds.length) {
      throw new Error('Some advisors not found or not authorized');
    }

    // Get document type from the document itself
    let documentType: string = 'will'; // default fallback
    
    // Try to determine document type from various tables
    const documentQueries = [
      supabase.from('legal_documents').select('type').eq('id', request.document_id).single(),
      supabase.from('insurance_documents').select('policy_type').eq('id', request.document_id).single(),
      supabase.from('financial_accounts').select('type').eq('id', request.document_id).single(),
      supabase.from('financial_assets').select('type').eq('id', request.document_id).single(),
      supabase.from('medical_records').select('record_type').eq('id', request.document_id).single(),
      supabase.from('health_appointments').select('appointment_type').eq('id', request.document_id).single(),
      supabase.from('medications').select('medication_type').eq('id', request.document_id).single(),
      supabase.from('pets').select('species').eq('id', request.document_id).single(),
      supabase.from('legacy_items').select('category').eq('id', request.document_id).single(),
      supabase.from('digital_estate_assets').select('asset_type').eq('id', request.document_id).single(),
      supabase.from('retirement_accounts').select('retirement_type').eq('id', request.document_id).single()
    ];

    for (const query of documentQueries) {
      const { data } = await query;
      if (data) {
        // Map the found type to our DocumentType
        if ('type' in data) {
          documentType = this.mapLegalDocumentType(data.type);
        } else if ('policy_type' in data) {
          documentType = 'insurance';
        } else if ('record_type' in data) {
          documentType = 'medical_record';
        } else if ('appointment_type' in data) {
          documentType = 'appointment';
        } else if ('medication_type' in data) {
          documentType = 'medication';
        } else if ('species' in data) {
          documentType = 'pet_profile';
        } else if ('category' in data) {
          documentType = 'legacy_item';
        } else if ('asset_type' in data) {
          documentType = 'digital_asset';
        } else if ('retirement_type' in data) {
          documentType = 'retirement';
        }
        break;
      }
    }

    // Delete existing permissions for this document
    await supabase
      .from('advisor_permissions')
      .update({ is_active: false })
      .eq('document_id', request.document_id)
      .in('advisor_id', advisorIds);

    // Create new permissions
    const permissionsToCreate = request.advisor_permissions.map(p => ({
      advisor_id: p.advisor_id,
      document_id: request.document_id,
      document_type: documentType,
      permission_type: p.permission_type,
      view_mode: p.view_mode,
      unlocker_id: p.unlocker_id,
      unlock_wait_period_days: p.unlock_wait_period_days || 30,
      is_active: true
    }));

    const { error } = await supabase
      .from('advisor_permissions')
      .insert(permissionsToCreate);

    if (error) throw error;

    // Log the permission changes
    for (const permission of request.advisor_permissions) {
      await this.logAuditAction({
        action: 'permission_updated',
        advisor_id: permission.advisor_id,
        document_id: request.document_id,
        document_type: documentType,
        details: {
          permission_type: permission.permission_type,
          view_mode: permission.view_mode,
          unlock_wait_period_days: permission.unlock_wait_period_days
        }
      });
    }
  }

  // Helper method to log audit actions
  private static async logAuditAction(logData: {
    action: AuditAction;
    advisor_id: string;
    document_id: string;
    document_type: string;
    details?: any;
  }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('advisor_audit_logs')
      .insert({
        user_id: user.id,
        advisor_id: logData.advisor_id,
        action: logData.action,
        document_id: logData.document_id,
        document_type: logData.document_type,
        action_details: logData.details || {}
      });
  }

  // ===== SHARED DOCUMENTS SYSTEM =====

  // Get documents shared with the current user as a trusted advisor
  static async getSharedDocumentsWithMe(): Promise<Array<{
    id: string;
    document_id: string;
    document_name: string;
    document_type: DocumentType;
    shared_by: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    permission_type: PermissionType;
    view_mode: ViewMode;
    shared_at: string;
    is_active: boolean;
  }>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, check if the current user is a trusted advisor for anyone
    const { data: advisorRelationships } = await supabase
      .from('trusted_advisors')
      .select(`
        id,
        user_id,
        first_name,
        last_name,
        email,
        status
      `)
      .eq('email', user.email)
      .eq('status', 'active');

    if (!advisorRelationships || advisorRelationships.length === 0) {
      return [];
    }

    // Get all documents shared with this advisor
    const sharedDocuments: Array<{
      id: string;
      document_id: string;
      document_name: string;
      document_type: DocumentType;
      shared_by: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
      };
      permission_type: PermissionType;
      view_mode: ViewMode;
      shared_at: string;
      is_active: boolean;
    }> = [];

    for (const relationship of advisorRelationships) {
      // Get permissions for this advisor
      const { data: permissions } = await supabase
        .from('advisor_permissions')
        .select('*')
        .eq('advisor_id', relationship.id)
        .eq('is_active', true)
        .neq('permission_type', 'do_not_share');

      if (permissions) {
        for (const permission of permissions) {
          // Get the document details based on type
          let documentName = 'Unknown Document';
          let documentType = permission.document_type as DocumentType;

          // Try to get document name from various tables
          const documentQueries = [
            supabase.from('legal_documents').select('title').eq('id', permission.document_id).single(),
            supabase.from('insurance_documents').select('policy_type, provider, policy_number').eq('id', permission.document_id).single(),
            supabase.from('financial_accounts').select('type, institution').eq('id', permission.document_id).single(),
            supabase.from('financial_assets').select('type, name').eq('id', permission.document_id).single(),
            supabase.from('medical_records').select('record_type, provider_name').eq('id', permission.document_id).single(),
            supabase.from('health_appointments').select('appointment_type, provider_name').eq('id', permission.document_id).single(),
            supabase.from('medications').select('medication_name, dosage').eq('id', permission.document_id).single(),
            supabase.from('pets').select('name, species, breed').eq('id', permission.document_id).single(),
            supabase.from('legacy_items').select('title, category').eq('id', permission.document_id).single(),
            supabase.from('digital_estate_assets').select('asset_type, asset_name').eq('id', permission.document_id).single(),
            supabase.from('retirement_accounts').select('retirement_type, provider').eq('id', permission.document_id).single()
          ];

          for (const query of documentQueries) {
            const { data } = await query;
            if (data) {
              if ('title' in data) {
                documentName = data.title;
              } else if ('policy_type' in data) {
                documentName = `${data.policy_type} - ${data.provider} (${data.policy_number})`;
              } else if ('type' in data && 'institution' in data) {
                documentName = `${data.type} - ${data.institution}`;
              } else if ('type' in data && 'name' in data) {
                documentName = `${data.type} - ${data.name}`;
              } else if ('record_type' in data && 'provider_name' in data) {
                documentName = `${data.record_type} - ${data.provider_name}`;
              } else if ('appointment_type' in data && 'provider_name' in data) {
                documentName = `${data.appointment_type} - ${data.provider_name}`;
              } else if ('medication_name' in data && 'dosage' in data) {
                documentName = `${data.medication_name} - ${data.dosage}`;
              } else if ('name' in data && 'species' in data) {
                documentName = `${data.species} - ${data.name} (${data.breed})`;
              } else if ('title' in data && 'category' in data) {
                documentName = `${data.category} - ${data.title}`;
              } else if ('asset_type' in data && 'asset_name' in data) {
                documentName = `${data.asset_type} - ${data.asset_name}`;
              } else if ('retirement_type' in data && 'provider' in data) {
                documentName = `${data.retirement_type} - ${data.provider}`;
              }
              break;
            }
          }

          // Get the user who shared this document
          const { data: userData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .eq('id', relationship.user_id)
            .single();

          if (userData) {
            sharedDocuments.push({
              id: permission.id,
              document_id: permission.document_id,
              document_name: documentName,
              document_type: documentType,
              shared_by: {
                id: userData.id,
                first_name: userData.first_name || 'Unknown',
                last_name: userData.last_name || 'User',
                email: userData.email || ''
              },
              permission_type: permission.permission_type,
              view_mode: permission.view_mode,
              shared_at: permission.created_at,
              is_active: permission.is_active
            });
          }
        }
      }
    }

    return sharedDocuments.sort((a, b) => new Date(b.shared_at).getTime() - new Date(a.shared_at).getTime());
  }

  // Get shared documents by a specific user
  static async getSharedDocumentsByUser(userId: string): Promise<Array<{
    id: string;
    document_id: string;
    document_name: string;
    document_type: DocumentType;
    shared_with: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    permission_type: PermissionType;
    view_mode: ViewMode;
    shared_at: string;
    is_active: boolean;
  }>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify the user is requesting their own shared documents
    if (user.id !== userId) {
      throw new Error('Not authorized to view other users shared documents');
    }

    // Get all advisors for this user
    const { data: advisors } = await supabase
      .from('trusted_advisors')
      .select('id, first_name, last_name, email')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (!advisors) return [];

    const sharedDocuments: Array<{
      id: string;
      document_id: string;
      document_name: string;
      document_type: DocumentType;
      shared_with: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
      };
      permission_type: PermissionType;
      view_mode: ViewMode;
      shared_at: string;
      is_active: boolean;
    }> = [];

    for (const advisor of advisors) {
      // Get permissions for this advisor
      const { data: permissions } = await supabase
        .from('advisor_permissions')
        .select('*')
        .eq('advisor_id', advisor.id)
        .eq('is_active', true)
        .neq('permission_type', 'do_not_share');

      if (permissions) {
        for (const permission of permissions) {
          // Get the document details based on type
          let documentName = 'Unknown Document';
          let documentType = permission.document_type as DocumentType;

          // Try to get document name from various tables
          const documentQueries = [
            supabase.from('legal_documents').select('title').eq('id', permission.document_id).single(),
            supabase.from('insurance_documents').select('policy_type, provider, policy_number').eq('id', permission.document_id).single(),
            supabase.from('financial_accounts').select('type, institution').eq('id', permission.document_id).single(),
            supabase.from('financial_assets').select('type, name').eq('id', permission.document_id).single(),
            supabase.from('medical_records').select('record_type, provider_name').eq('id', permission.document_id).single(),
            supabase.from('health_appointments').select('appointment_type, provider_name').eq('id', permission.document_id).single(),
            supabase.from('medications').select('medication_name, dosage').eq('id', permission.document_id).single(),
            supabase.from('pets').select('name, species, breed').eq('id', permission.document_id).single(),
            supabase.from('legacy_items').select('title, category').eq('id', permission.document_id).single(),
            supabase.from('digital_estate_assets').select('asset_type, asset_name').eq('id', permission.document_id).single(),
            supabase.from('retirement_accounts').select('retirement_type, provider').eq('id', permission.document_id).single()
          ];

          for (const query of documentQueries) {
            const { data } = await query;
            if (data) {
              if ('title' in data) {
                documentName = data.title;
              } else if ('policy_type' in data) {
                documentName = `${data.policy_type} - ${data.provider} (${data.policy_number})`;
              } else if ('type' in data && 'institution' in data) {
                documentName = `${data.type} - ${data.institution}`;
              } else if ('type' in data && 'name' in data) {
                documentName = `${data.type} - ${data.name}`;
              } else if ('record_type' in data && 'provider_name' in data) {
                documentName = `${data.record_type} - ${data.provider_name}`;
              } else if ('appointment_type' in data && 'provider_name' in data) {
                documentName = `${data.appointment_type} - ${data.provider_name}`;
              } else if ('medication_name' in data && 'dosage' in data) {
                documentName = `${data.medication_name} - ${data.dosage}`;
              } else if ('name' in data && 'species' in data) {
                documentName = `${data.species} - ${data.name} (${data.breed})`;
              } else if ('title' in data && 'category' in data) {
                documentName = `${data.category} - ${data.title}`;
              } else if ('asset_type' in data && 'asset_name' in data) {
                documentName = `${data.asset_type} - ${data.asset_name}`;
              } else if ('retirement_type' in data && 'provider' in data) {
                documentName = `${data.retirement_type} - ${data.provider}`;
              }
              break;
            }
          }

          sharedDocuments.push({
            id: permission.id,
            document_id: permission.document_id,
            document_name: documentName,
            document_type: documentType,
            shared_with: {
              id: advisor.id,
              first_name: advisor.first_name,
              last_name: advisor.last_name,
              email: advisor.email
            },
            permission_type: permission.permission_type,
            view_mode: permission.view_mode,
            shared_at: permission.created_at,
            is_active: permission.is_active
          });
        }
      }
    }

    return sharedDocuments.sort((a, b) => new Date(b.shared_at).getTime() - new Date(a.shared_at).getTime());
  }

  // Auto-share documents when a user registers with an email that was previously invited
  static async processPendingSharesForNewUser(userEmail: string): Promise<void> {
    // Find all pending trusted advisor invitations for this email
    const { data: pendingInvitations } = await supabase
      .from('trusted_advisors')
      .select('*')
      .eq('email', userEmail)
      .eq('status', 'pending');

    if (!pendingInvitations || pendingInvitations.length === 0) {
      return;
    }

    // Get the new user's ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    for (const invitation of pendingInvitations) {
      // Update the invitation to link to the new user
      await supabase
        .from('trusted_advisors')
        .update({
          status: 'active',
          user_id: user.id,
          accepted_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      // Send notification to the person who invited them
      await this.logAuditAction({
        action: 'advisor_accepted_invitation',
        advisor_id: invitation.id,
        document_id: '',
        document_type: 'system',
        details: {
          invited_email: userEmail,
          accepted_at: new Date().toISOString()
        }
      });
    }
  }
} 